function normalizeText(text) {
  if (!text) return "";
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function getLocalePrefix() {
  const match = window.location.pathname.match(/^\/([a-z]{2}(-[a-z]{2})?)\//i);
  return match ? `/${match[1]}` : "";
}

const wineTypeTranslations = {
  "": {
    "Blanc": "Blanc",
    "Rouge": "Rouge",
    "Rosé": "Rosé",
    "Effervescent": "Effervescent",
    "Liquoreux": "Liquoreux",
    "Orange": "Orange"
  },
  "de": {
    "Blanc": "Weisswein",
    "Rouge": "Rotwein",
    "Rosé": "Roséwein",
    "Effervescent": "Schaumwein",
    "Liquoreux": "Süsswein",
    "Orange": "Orangewein"
  }
};

function getCurrentLangKey() {
  const prefix = getLocalePrefix().replace("/", "");
  return prefix;
}

function translateWineType(label) {
  const lang = getCurrentLangKey();
  const dict = wineTypeTranslations[lang];
  if (dict && dict[label]) {
    return dict[label];
  }
  return label;
}

function buildSynonymLookup(groups) {
  const map = {};
  groups.forEach(group => {
    group.forEach(variant => {
      map[normalizeText(variant)] = group;
    });
  });
  return map;
}

function getSynonyms(lookup, name) {
  const key = normalizeText(name);
  return lookup[key] || [name];
}

const countrySynonymGroups = [
  ["Suisse", "Switzerland", "Schweiz"],
  ["France", "Frankreich"],
  ["Allemagne", "Germany", "Deutschland", "Deutsch"],
  ["Italie", "Italy", "Italien"],
  ["Espagne", "Spain", "Spanien"],
  ["Portugal"],
  ["Greece", "Grèce", "Griechenland"],
  ["Austria", "Autriche", "Österreich"],
  ["Hungary", "Hongrie", "Ungarn"]
];

const countryLookup = buildSynonymLookup(countrySynonymGroups);

function getCountrySynonyms(name) {
  return getSynonyms(countryLookup, name);
}

const regionSynonymGroups = [
  ["Sicile", "Sicily", "Sizilien"],
  ["Sud-Ouest", "Southwest France", "Südwestfrankreich"],
  ["Toscane", "Tuscany", "Toskana"],
  ["Pfalz", "Palatinate", "Pfalz"],
  ["Bourgogne", "Burgundy", "Burgund"],
  ["Ribera del Duero"],
  ["Valais", "Wallis"],
  ["Alsace", "Elsass"],
  ["Piémont", "Piedmont", "Piemont"],
  ["Galice", "Galicia", "Galizien"],
  ["Champagne", "Champán", "Champagner"],
  ["Beaujolais"],
  ["Côtes de Provence", "Provence"],
  ["Corsica", "Corse", "Korsika"],
  ["Rhône Valley", "Vallée du Rhône", "Rhonetal", "Rhône-Tal"],
  ["Kamptal"],
  ["Tokaj", "Tokaji"],
  ["Loire Valley", "Vallée de la Loire", "Loiretal"],
  ["Languedoc-Roussillon"],
  ["Bordeaux", "Bordó"]
];

const regionLookup = buildSynonymLookup(regionSynonymGroups);

function getRegionSynonyms(name) {
  return getSynonyms(regionLookup, name);
}


const wineTypeSynonymGroups = Object.keys(wineTypeTranslations[""]).map(frKey => {
  const variants = new Set([frKey, wineTypeTranslations[""][frKey]]);
  if (wineTypeTranslations.de && wineTypeTranslations.de[frKey]) {
    variants.add(wineTypeTranslations.de[frKey]);
  }
  return Array.from(variants);
});

const wineTypeLookup = buildSynonymLookup(wineTypeSynonymGroups);

function getWineTypeSynonyms(name) {
  return getSynonyms(wineTypeLookup, name);
}

const wineTypeOrder = ["Rouge", "Blanc", "Rosé", "Effervescent", "Liquoreux", "Orange"];

function buildWineTypeOrderLookup() {
  const map = {};
  wineTypeOrder.forEach((frKey, idx) => {
    const group = getWineTypeSynonyms(frKey);
    group.forEach(variant => {
      map[normalizeText(variant)] = idx;
    });
  });
  return map;
}

const wineTypeOrderLookup = buildWineTypeOrderLookup();

function getWineTypeOrderIndex(label) {
  const idx = wineTypeOrderLookup[normalizeText(label)];
  return idx !== undefined ? idx : 999; 
}
/* --------------------------------------------------------------------------------------------- */

const sweetnessSynonymGroups = [
  ["Sec", "Dry", "Trocken"],
  ["Demi-sec", "Off-dry", "Halbtrocken"],
  ["Moelleux", "Medium sweet", "Lieblich"],
  ["Doux", "Sweet", "Süss"]
];

const sweetnessLookup = buildSynonymLookup(sweetnessSynonymGroups);

function getSweetnessSynonyms(name) {
  return getSynonyms(sweetnessLookup, name);
}

/* ============================================================ */

async function fetchCollectionProducts(collectionHandle) {
  const STOREFRONT_TOKEN = "cbbc508acd4a3899d3a86048e5f42b10";
  const SHOP_DOMAIN = "karafe.ch";
  const endpoint = `https://${SHOP_DOMAIN}/api/2025-01/graphql.json`;

  function getShopifyLanguageCode() {
    const lang = getCurrentLangKey();
    const map = {
      "": "FR",
      "de": "DE"
    };
    return map[lang] || "FR";
  }

  const fetchPage = async (cursor = null) => {
    const afterClause = cursor ? `, after: "${cursor}"` : "";
    const language = getShopifyLanguageCode();

    const query = `
      query getCollectionProducts($language: LanguageCode)
      @inContext(language: $language) {
        collection(handle: "${collectionHandle}") {
          products(first: 250${afterClause}) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                title
                handle
                images(first: 1) { edges { node { src } } }
                priceRange { minVariantPrice { amount currencyCode } }
                variants(first: 10) { edges { node { availableForSale quantityAvailable } } }
                wineVariety: metafield(namespace: "shopify", key: "wine-variety") {
                  value
                  references(first: 10) {
                    edges {
                      node {
                        ... on Metaobject {
                          id
                          handle
                          fields { key value }
                        }
                      }
                    }
                  }
                }
                region: metafield(namespace: "shopify", key: "region") {
                  value
                  references(first: 10) {
                    edges {
                      node {
                        ... on Metaobject {
                          id
                          handle
                          fields { key value }
                        }
                      }
                    }
                  }
                }
                wineSweetness: metafield(namespace: "shopify", key: "wine-sweetness") {
                  value
                  references(first: 10) {
                    edges {
                      node {
                        ... on Metaobject {
                          id
                          handle
                          fields { key value }
                        }
                      }
                    }
                  }
                }
                country: metafield(namespace: "shopify", key: "country") {
                  value
                  references(first: 10) {
                    edges {
                      node {
                        ... on Metaobject {
                          id
                          handle
                          fields { key value }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query,
        variables: { language }
      }),
    });

    return await res.json();
  };

  try {
    const container = document.getElementById("products-container");
    container.innerHTML = "";

    const wineTypesSet = new Set();
    const allProducts = [];

    let hasNextPage = true;
    let cursor = null;

    while (hasNextPage) {
      const json = await fetchPage(cursor);
      const productsData = json.data.collection.products;

      hasNextPage = productsData.pageInfo.hasNextPage;
      cursor = productsData.pageInfo.endCursor;

      productsData.edges.forEach(({ node }) => {
        const available = node.variants.edges.some(v => v.node.quantityAvailable > 0);
        const stockLabel = available ? '' : `<span class="product__badge product__badge--sold">${window.krfSoldOutLabel}</span>`;

        const wineVarietyNames = [];
        if (node.wineVariety?.references?.edges) {
          node.wineVariety.references.edges.forEach(({ node: metaNode }) => {
            const labelField = metaNode.fields.find(f => f.key === "label");
            if (labelField) {
              wineVarietyNames.push(labelField.value);
              wineTypesSet.add(labelField.value);
            }
          });
        }

        const regionNames = [];
        if (node.region?.references?.edges) {
          node.region.references.edges.forEach(({ node: metaNode }) => {
            const labelField = metaNode.fields.find(f => f.key === "label");
            if (labelField) regionNames.push(labelField.value);
          });
        }

        const sweetnessNames = [];
        if (node.wineSweetness?.references?.edges) {
          node.wineSweetness.references.edges.forEach(({ node: metaNode }) => {
            const labelField = metaNode.fields.find(f => f.key === "label");
            if (labelField) sweetnessNames.push(labelField.value);
          });
        }

        const countryNames = [];
        if (node.country?.references?.edges) {
          node.country.references.edges.forEach(({ node: metaNode }) => {
            const labelField = metaNode.fields.find(f => f.key === "label");
            if (labelField) countryNames.push(labelField.value);
          });
        }

        const countrySynonyms = countryNames.flatMap(name => getCountrySynonyms(name));
        const regionSynonyms = regionNames.flatMap(name => getRegionSynonyms(name));
        const wineVarietySynonyms = wineVarietyNames.flatMap(name => getWineTypeSynonyms(name));
        const sweetnessSynonyms = sweetnessNames.flatMap(name => getSweetnessSynonyms(name));

        const productHTML = document.createElement("div");
        productHTML.className = "product-item";
        productHTML.dataset.price = parseFloat(node.priceRange.minVariantPrice.amount) * 100;
        productHTML.dataset.inStock = available ? 1 : 0;
        productHTML.dataset.wineVarieties = wineVarietyNames.join(",");
        productHTML.dataset.regions = regionNames.join(",");
        productHTML.dataset.sweetness = sweetnessNames.join(",");
        productHTML.dataset.country = countryNames.join(",");
        productHTML.dataset.title = node.title.toLowerCase();
        productHTML.dataset.titleNormalized = normalizeText(node.title);
        productHTML.dataset.wineVarietiesNormalized = normalizeText(wineVarietySynonyms.join(","));
        productHTML.dataset.regionsNormalized = normalizeText(regionSynonyms.join(","));
        productHTML.dataset.sweetnessNormalized = normalizeText(sweetnessSynonyms.join(","));
        productHTML.dataset.countryNormalized = normalizeText(countrySynonyms.join(","));

        const localePrefix = getLocalePrefix();

        productHTML.innerHTML = `
          <a href="${localePrefix}/products/${node.handle}" class="product-image-link">
            <div class="product-image-wrapper">
              <img src="${node.images.edges[0]?.node.src}" alt="${node.title}" />
              ${stockLabel}
            </div>
          </a>
          <a class="grid__caption text-center" href="${localePrefix}/products/${node.handle}">
            <p class="name_wrapper product-item-title" style="padding-top: 5px;">${node.title}</p>
            <p class="price_wrapper">
              ${node.priceRange.minVariantPrice.currencyCode} ${parseFloat(node.priceRange.minVariantPrice.amount).toFixed(2)}
            </p>
          </a>
        `;

        container.appendChild(productHTML);
        allProducts.push(productHTML);
      });
    }

    const wineTypes = Array.from(wineTypesSet).sort(
      (a, b) => getWineTypeOrderIndex(a) - getWineTypeOrderIndex(b)
    );
    generateWineTypeFilters(wineTypes);
    initFilters(allProducts);
    initMobileFilters(allProducts);
    initSearchBar(allProducts);

  } catch (err) {
    console.error("❌ Error cargando productos:", err);
  }
}

function initSearchBar(allProducts) {
  const searchInput = document.getElementById("krf-search-input");
  const searchBtn = document.getElementById("krf-search-btn");
  const clearBtn = document.getElementById("krf-clear-search");

  const searchInputMobile = document.getElementById("krf-search-input-mobile");
  const searchBtnMobile = document.getElementById("krf-search-btn-mobile");
  const clearBtnMobile = document.getElementById("krf-clear-search-mobile");

  window.currentSearchTerm = "";
  window.currentSearchTermNormalized = "";

  function performSearch(source = 'desktop') {
    const input = source === 'mobile' ? searchInputMobile : searchInput;
    const rawSearchTerm = input?.value.trim() || "";

    window.currentSearchTerm = rawSearchTerm.toLowerCase();
    window.currentSearchTermNormalized = normalizeText(rawSearchTerm);

    if (searchInput) searchInput.value = rawSearchTerm;
    if (searchInputMobile) searchInputMobile.value = rawSearchTerm;

    if (window.currentSearchTerm === "") {
      if (clearBtn) clearBtn.style.display = "none";
      if (clearBtnMobile) clearBtnMobile.style.display = "none";
    } else {
      if (clearBtn) clearBtn.style.display = "inline-block";
      if (clearBtnMobile) clearBtnMobile.style.display = "block";
    }

    applyFiltersAndSort();
  }

  function clearSearch() {
    if (searchInput) searchInput.value = "";
    if (searchInputMobile) searchInputMobile.value = "";
    window.currentSearchTerm = "";
    window.currentSearchTermNormalized = "";
    if (clearBtn) clearBtn.style.display = "none";
    if (clearBtnMobile) clearBtnMobile.style.display = "none";
    applyFiltersAndSort();
  }

  if (searchBtn) searchBtn.addEventListener("click", () => performSearch('desktop'));
  if (clearBtn) clearBtn.addEventListener("click", clearSearch);
  if (searchInput) {
    searchInput.addEventListener("keypress", (e) => {
      if (e.key === "Enter") performSearch('desktop');
    });
    searchInput.addEventListener("input", () => {
      if (searchInput.value.trim() === "") clearSearch();
    });
  }

  if (searchBtnMobile) searchBtnMobile.addEventListener("click", () => performSearch('mobile'));
  if (clearBtnMobile) clearBtnMobile.addEventListener("click", clearSearch);
  if (searchInputMobile) {
    searchInputMobile.addEventListener("keypress", (e) => {
      if (e.key === "Enter") performSearch('mobile');
    });
    searchInputMobile.addEventListener("input", () => {
      if (searchInputMobile.value.trim() === "") clearSearch();
    });
  }
}

function generateWineTypeFilters(wineTypes) {
  const desktopContainer = document.getElementById("wine-types-desktop");
  const mobileContainer = document.getElementById("wine-types-mobile");

  if (!desktopContainer || !mobileContainer) {
    console.error("❌ No se encontraron los contenedores de tipos de vino");
    return;
  }

  desktopContainer.innerHTML = "";
  mobileContainer.innerHTML = "";

  wineTypes.forEach(type => {
    const sanitizedId = type.toLowerCase().replace(/[^a-z0-9]/g, "-");
    const displayLabel = translateWineType(type);

    const desktopOption = document.createElement("div");
    desktopOption.className = "filter__option";
    desktopOption.innerHTML = `
      <input type="checkbox" id="type-${sanitizedId}" value="${type}" />
      <label for="type-${sanitizedId}">
        <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-check-slim" viewBox="0 0 14 11">
          <path d="m-.046 6.89.707-.708 2.95 2.95 9.192-9.193.707.707-9.9 9.9-3.656-3.657z" fill-rule="evenodd"></path>
        </svg>
        ${displayLabel}
      </label>
    `;
    desktopContainer.appendChild(desktopOption);

    const mobileOption = document.createElement("div");
    mobileOption.className = "filter__option";
    mobileOption.innerHTML = `
      <input type="checkbox" id="type-${sanitizedId}-mobile" value="${type}" />
      <label for="type-${sanitizedId}-mobile">
        <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-check-slim" viewBox="0 0 14 11">
          <path d="m-.046 6.89.707-.708 2.95 2.95 9.192-9.193.707.707-9.9 9.9-3.656-3.657z" fill-rule="evenodd"></path>
        </svg>
        ${displayLabel}
      </label>
    `;
    mobileContainer.appendChild(mobileOption);
  });

  syncDynamicFilters(wineTypes);
}

function syncDynamicFilters(wineTypes) {
  wineTypes.forEach(type => {
    const sanitizedId = type.toLowerCase().replace(/[^a-z0-9]/g, "-");
    const desktop = document.getElementById(`type-${sanitizedId}`);
    const mobile = document.getElementById(`type-${sanitizedId}-mobile`);

    if (desktop && mobile) {
      desktop.addEventListener("change", () => { mobile.checked = desktop.checked; });
      mobile.addEventListener("change", () => { desktop.checked = mobile.checked; });
    }
  });
}

function initFilters(allProducts) {
  const container = document.getElementById("products-container");
  const totalSpan = document.getElementById('total-products-count');

  const inStockCheckbox = document.getElementById("in-stock-checkbox");
  const outOfStockCheckbox = document.getElementById("out-of-stock-checkbox");
  const priceMinInput = document.getElementById("price-min");
  const priceMaxInput = document.getElementById("price-max");
  const priceSliderMin = document.getElementById("price-slider-min");
  const priceSliderMax = document.getElementById("price-slider-max");
  const sortOptionsDesktop = document.getElementById("sort-options");

  const getWineCheckboxes = () => {
    return Array.from(document.querySelectorAll('#wine-types-desktop input[type="checkbox"]'));
  };

  function updateDualSliderBackground() {
    const min = parseInt(priceSliderMin.min, 10);
    const max = parseInt(priceSliderMax.max, 10);
    const minVal = parseInt(priceSliderMin.value, 10);
    const maxVal = parseInt(priceSliderMax.value, 10);
    const percentMin = ((minVal - min) / (max - min)) * 100;
    const percentMax = ((maxVal - min) / (max - min)) * 100;
    const gradient = `linear-gradient(to right, #b1ad88 0%, #b1ad88 ${percentMin}%, #000 ${percentMin}%, #000 ${percentMax}%, #b1ad88 ${percentMax}%, #b1ad88 100%)`;
    priceSliderMin.style.background = gradient;
    priceSliderMax.style.background = gradient;
  }

  window.applyFiltersAndSort = function applyFiltersAndSort() {
    const minPrice = parseFloat(priceMinInput.value) || 0;
    const maxPrice = parseFloat(priceMaxInput.value) || 5000;

    const wineCheckboxes = getWineCheckboxes();
    const selectedTypes = wineCheckboxes.filter(cb => cb.checked).map(cb => cb.value.trim());

    const searchTerm = window.currentSearchTermNormalized || "";

    const filtered = allProducts.filter(item => {
      const price = parseFloat(item.dataset.price);
      const inStock = parseInt(item.dataset.inStock, 10);
      const productTypes = (item.dataset.wineVarieties || "").split(",").map(s => s.trim()).filter(Boolean);

      const isPriceValid = price >= minPrice * 100 && price <= maxPrice * 100;

      let isStockValid = true;
      if (inStockCheckbox.checked && !outOfStockCheckbox.checked) isStockValid = inStock > 0;
      else if (!inStockCheckbox.checked && outOfStockCheckbox.checked) isStockValid = inStock === 0;

      const isTypeValid = selectedTypes.length === 0 ||
        (productTypes.length > 0 && selectedTypes.some(type => productTypes.includes(type)));

      let isSearchValid = true;
      if (searchTerm !== "") {
        const title = item.dataset.titleNormalized || "";
        const varieties = item.dataset.wineVarietiesNormalized || "";
        const regions = item.dataset.regionsNormalized || "";
        const sweetness = item.dataset.sweetnessNormalized || "";
        const country = item.dataset.countryNormalized || "";

        isSearchValid = title.includes(searchTerm) ||
          varieties.includes(searchTerm) ||
          regions.includes(searchTerm) ||
          sweetness.includes(searchTerm) ||
          country.includes(searchTerm);
      }

      return isPriceValid && isStockValid && isTypeValid && isSearchValid;
    });

    container.innerHTML = "";
    if (filtered.length === 0) {
      document.getElementById("no-results-message").style.display = "block";
    } else {
      document.getElementById("no-results-message").style.display = "none";
      filtered.forEach(p => container.appendChild(p));
    }

    if (totalSpan) totalSpan.textContent = `${filtered.length} ${window.krfResultsLabel}`;
    const totalSpanMobile = document.getElementById('total-products-count-mobile');
    if (totalSpanMobile) totalSpanMobile.textContent = `${filtered.length} ${window.krfResultsLabel}`;
  };

  document.getElementById("wine-types-desktop")?.addEventListener("change", (e) => {
    if (e.target.type === "checkbox") applyFiltersAndSort();
  });

  [inStockCheckbox, outOfStockCheckbox].forEach(cb => cb?.addEventListener("change", applyFiltersAndSort));

  if (priceSliderMin && priceSliderMax) {
    priceSliderMin.addEventListener("input", () => {
      if (parseInt(priceSliderMin.value) > parseInt(priceSliderMax.value)) priceSliderMin.value = priceSliderMax.value;
      priceMinInput.value = priceSliderMin.value;
      updateDualSliderBackground();
      applyFiltersAndSort();
    });

    priceSliderMax.addEventListener("input", () => {
      if (parseInt(priceSliderMax.value) < parseInt(priceSliderMin.value)) priceSliderMax.value = priceSliderMin.value;
      priceMaxInput.value = priceSliderMax.value;
      updateDualSliderBackground();
      applyFiltersAndSort();
    });
  }

  if (priceMinInput && priceMaxInput) {
    priceMinInput.addEventListener("input", () => {
      if (priceSliderMin) priceSliderMin.value = priceMinInput.value;
      updateDualSliderBackground();
      applyFiltersAndSort();
    });
    priceMaxInput.addEventListener("input", () => {
      if (priceSliderMax) priceSliderMax.value = priceMaxInput.value;
      updateDualSliderBackground();
      applyFiltersAndSort();
    });
  }

  if (sortOptionsDesktop) {
    sortOptionsDesktop.addEventListener("change", () => {
      const value = sortOptionsDesktop.value;
      allProducts.sort((a, b) => {
        const priceA = parseFloat(a.dataset.price);
        const priceB = parseFloat(b.dataset.price);
        const titleA = a.querySelector(".product-item-title").textContent.toLowerCase();
        const titleB = b.querySelector(".product-item-title").textContent.toLowerCase();
        switch (value) {
          case "price-asc": return priceA - priceB;
          case "price-desc": return priceB - priceA;
          case "name-asc": return titleA.localeCompare(titleB);
          case "name-desc": return titleB.localeCompare(titleA);
          default: return 0;
        }
      });
      applyFiltersAndSort();
    });
  }

  if (priceSliderMin && priceSliderMax) updateDualSliderBackground();
  applyFiltersAndSort();
}

function initMobileFilters(allProducts) {
  const container = document.getElementById("products-container");
  const totalSpanMobile = document.getElementById('total-products-count-mobile');

  const inStockCheckboxMobile = document.getElementById("in-stock-checkbox-mobile");
  const outOfStockCheckboxMobile = document.getElementById("out-of-stock-checkbox-mobile");
  const priceMinInputMobile = document.getElementById("price-min-mobile");
  const priceMaxInputMobile = document.getElementById("price-max-mobile");
  const priceSliderMinMobile = document.getElementById("price-slider-min-mobile");
  const priceSliderMaxMobile = document.getElementById("price-slider-max-mobile");
  const sortOptionsMobile = document.getElementById("sort-options-mobile");

  const getWineCheckboxesMobile = () => {
    return Array.from(document.querySelectorAll('#wine-types-mobile input[type="checkbox"]'));
  };

  const modal = document.getElementById("myModal");
  const openModalBtn = document.getElementById("openModal");
  const closeBtn = document.querySelector(".krf-close-btn");
  const searchBtn = document.getElementById("mobile-search-btn");

  function updateDualSliderBackgroundMobile() {
    if (!priceSliderMinMobile || !priceSliderMaxMobile) return;
    const min = parseInt(priceSliderMinMobile.min, 10);
    const max = parseInt(priceSliderMaxMobile.max, 10);
    const minVal = parseInt(priceSliderMinMobile.value, 10);
    const maxVal = parseInt(priceSliderMaxMobile.value, 10);
    const percentMin = ((minVal - min) / (max - min)) * 100;
    const percentMax = ((maxVal - min) / (max - min)) * 100;
    const gradient = `linear-gradient(to right, #b1ad88 0%, #b1ad88 ${percentMin}%, #000 ${percentMin}%, #000 ${percentMax}%, #b1ad88 ${percentMax}%, #b1ad88 100%)`;
    priceSliderMinMobile.style.background = gradient;
    priceSliderMaxMobile.style.background = gradient;
  }

  function applyFiltersAndSortMobile() {
    const minPrice = parseFloat(priceMinInputMobile.value) || 0;
    const maxPrice = parseFloat(priceMaxInputMobile.value) || 5000;

    const wineCheckboxesMobile = getWineCheckboxesMobile();
    const selectedTypesMobile = wineCheckboxesMobile.filter(cb => cb.checked).map(cb => cb.value.trim());

    const searchTerm = window.currentSearchTermNormalized || "";

    const filtered = allProducts.filter(item => {
      const price = parseFloat(item.dataset.price);
      const inStock = parseInt(item.dataset.inStock, 10);
      const productTypes = (item.dataset.wineVarieties || "").split(",").map(s => s.trim()).filter(Boolean);

      const isPriceValid = price >= minPrice * 100 && price <= maxPrice * 100;

      let isStockValid = true;
      if (inStockCheckboxMobile.checked && !outOfStockCheckboxMobile.checked) isStockValid = inStock > 0;
      else if (!inStockCheckboxMobile.checked && outOfStockCheckboxMobile.checked) isStockValid = inStock === 0;

      const isTypeValid = selectedTypesMobile.length === 0 ||
        (productTypes.length > 0 && selectedTypesMobile.some(type => productTypes.includes(type)));

      let isSearchValid = true;
      if (searchTerm !== "") {
        const title = item.dataset.titleNormalized || "";
        const varieties = item.dataset.wineVarietiesNormalized || "";
        const regions = item.dataset.regionsNormalized || "";
        const sweetness = item.dataset.sweetnessNormalized || "";
        const country = item.dataset.countryNormalized || "";

        isSearchValid = title.includes(searchTerm) ||
          varieties.includes(searchTerm) ||
          regions.includes(searchTerm) ||
          sweetness.includes(searchTerm) ||
          country.includes(searchTerm);
      }

      return isPriceValid && isStockValid && isTypeValid && isSearchValid;
    });

    container.innerHTML = "";
    if (filtered.length === 0) {
      document.getElementById("no-results-message").style.display = "block";
    } else {
      document.getElementById("no-results-message").style.display = "none";
      filtered.forEach(p => container.appendChild(p));
    }

    if (totalSpanMobile) totalSpanMobile.textContent = `${filtered.length} ${window.krfResultsLabel}`;
    const totalSpan = document.getElementById('total-products-count');
    if (totalSpan) totalSpan.textContent = `${filtered.length} ${window.krfResultsLabel}`;
  }

  document.getElementById("wine-types-mobile")?.addEventListener("change", (e) => {
    if (e.target.type === "checkbox") applyFiltersAndSortMobile();
  });

  [inStockCheckboxMobile, outOfStockCheckboxMobile].forEach(cb => {
    if (cb) cb.addEventListener("change", applyFiltersAndSortMobile);
  });

  if (priceSliderMinMobile && priceSliderMaxMobile) {
    priceSliderMinMobile.addEventListener("input", () => {
      if (parseInt(priceSliderMinMobile.value) > parseInt(priceSliderMaxMobile.value)) {
        priceSliderMinMobile.value = priceSliderMaxMobile.value;
      }
      priceMinInputMobile.value = priceSliderMinMobile.value;
      updateDualSliderBackgroundMobile();
      applyFiltersAndSortMobile();
    });

    priceSliderMaxMobile.addEventListener("input", () => {
      if (parseInt(priceSliderMaxMobile.value) < parseInt(priceSliderMinMobile.value)) {
        priceSliderMaxMobile.value = priceSliderMinMobile.value;
      }
      priceMaxInputMobile.value = priceSliderMaxMobile.value;
      updateDualSliderBackgroundMobile();
      applyFiltersAndSortMobile();
    });
  }

  if (priceMinInputMobile && priceMaxInputMobile) {
    priceMinInputMobile.addEventListener("input", () => {
      if (priceSliderMinMobile) priceSliderMinMobile.value = priceMinInputMobile.value;
      updateDualSliderBackgroundMobile();
      applyFiltersAndSortMobile();
    });
    priceMaxInputMobile.addEventListener("input", () => {
      if (priceSliderMaxMobile) priceSliderMaxMobile.value = priceMaxInputMobile.value;
      updateDualSliderBackgroundMobile();
      applyFiltersAndSortMobile();
    });
  }

  if (sortOptionsMobile) {
    sortOptionsMobile.addEventListener("change", () => {
      const value = sortOptionsMobile.value;
      allProducts.sort((a, b) => {
        const priceA = parseFloat(a.dataset.price);
        const priceB = parseFloat(b.dataset.price);
        const titleA = a.querySelector(".product-item-title").textContent.toLowerCase();
        const titleB = b.querySelector(".product-item-title").textContent.toLowerCase();
        switch (value) {
          case "price-asc": return priceA - priceB;
          case "price-desc": return priceB - priceA;
          case "name-asc": return titleA.localeCompare(titleB);
          case "name-desc": return titleB.localeCompare(titleA);
          default: return 0;
        }
      });
      applyFiltersAndSortMobile();
    });
  }

  if (openModalBtn && modal) {
    openModalBtn.addEventListener("click", () => { modal.style.display = "block"; });
  }

  if (closeBtn && modal) {
    closeBtn.addEventListener("click", () => { modal.style.display = "none"; });
  }

  if (searchBtn && modal) {
    searchBtn.addEventListener("click", () => {
      const searchInputMobile = document.getElementById("krf-search-input-mobile");
      if (searchInputMobile && searchInputMobile.value.trim() !== "") {
        const rawSearchTerm = searchInputMobile.value.trim();
        window.currentSearchTerm = rawSearchTerm.toLowerCase();
        window.currentSearchTermNormalized = normalizeText(rawSearchTerm);

        const searchInput = document.getElementById("krf-search-input");
        if (searchInput) searchInput.value = rawSearchTerm;

        const clearBtn = document.getElementById("krf-clear-search");
        const clearBtnMobile = document.getElementById("krf-clear-search-mobile");
        if (clearBtn) clearBtn.style.display = "inline-block";
        if (clearBtnMobile) clearBtnMobile.style.display = "block";
      }

      applyFiltersAndSortMobile();
      modal.style.display = "none";
    });
  }

  if (modal) {
    window.addEventListener("click", (event) => {
      if (event.target === modal) modal.style.display = "none";
    });
  }

  if (priceSliderMinMobile && priceSliderMaxMobile) updateDualSliderBackgroundMobile();
  applyFiltersAndSortMobile();
}

function syncFilters() {
  const inStockDesktop = document.getElementById("in-stock-checkbox");
  const inStockMobile = document.getElementById("in-stock-checkbox-mobile");
  const outStockDesktop = document.getElementById("out-of-stock-checkbox");
  const outStockMobile = document.getElementById("out-of-stock-checkbox-mobile");

  if (inStockDesktop && inStockMobile) {
    inStockDesktop.addEventListener("change", () => { inStockMobile.checked = inStockDesktop.checked; });
    inStockMobile.addEventListener("change", () => { inStockDesktop.checked = inStockMobile.checked; });
  }

  if (outStockDesktop && outStockMobile) {
    outStockDesktop.addEventListener("change", () => { outStockMobile.checked = outStockDesktop.checked; });
    outStockMobile.addEventListener("change", () => { outStockDesktop.checked = outStockMobile.checked; });
  }

  const priceElements = [
    { desktop: "price-min", mobile: "price-min-mobile" },
    { desktop: "price-max", mobile: "price-max-mobile" },
    { desktop: "price-slider-min", mobile: "price-slider-min-mobile" },
    { desktop: "price-slider-max", mobile: "price-slider-max-mobile" }
  ];

  priceElements.forEach(({ desktop, mobile }) => {
    const desktopEl = document.getElementById(desktop);
    const mobileEl = document.getElementById(mobile);
    if (desktopEl && mobileEl) {
      desktopEl.addEventListener("input", () => { mobileEl.value = desktopEl.value; });
      mobileEl.addEventListener("input", () => { desktopEl.value = mobileEl.value; });
    }
  });

  const sortDesktop = document.getElementById("sort-options");
  const sortMobile = document.getElementById("sort-options-mobile");
  if (sortDesktop && sortMobile) {
    sortDesktop.addEventListener("change", () => { sortMobile.value = sortDesktop.value; });
    sortMobile.addEventListener("change", () => { sortDesktop.value = sortMobile.value; });
  }
}

function resetDesktopFilters() {
  const inStockDesktop = document.getElementById("in-stock-checkbox");
  const outStockDesktop = document.getElementById("out-of-stock-checkbox");
  if (inStockDesktop) inStockDesktop.checked = false;
  if (outStockDesktop) outStockDesktop.checked = false;

  document.querySelectorAll('#wine-types-desktop input[type="checkbox"]').forEach(cb => { cb.checked = false; });

  const priceMinInput = document.getElementById("price-min");
  const priceMaxInput = document.getElementById("price-max");
  const maxPrice = window.maxProductPrice || 5000;
  if (priceMinInput) priceMinInput.value = 0;
  if (priceMaxInput) priceMaxInput.value = maxPrice;

  const priceSliderMin = document.getElementById("price-slider-min");
  const priceSliderMax = document.getElementById("price-slider-max");
  if (priceSliderMin) priceSliderMin.value = 0;
  if (priceSliderMax) priceSliderMax.value = maxPrice;

  const sortDesktop = document.getElementById("sort-options");
  if (sortDesktop) sortDesktop.value = "default";

  const searchInput = document.getElementById("krf-search-input");
  const clearBtn = document.getElementById("krf-clear-search");
  if (searchInput) searchInput.value = "";
  if (clearBtn) clearBtn.style.display = "none";

  if (priceMinInput) priceMinInput.dispatchEvent(new Event('input'));
  if (priceMaxInput) priceMaxInput.dispatchEvent(new Event('input'));
}

function resetMobileFilters() {
  const inStockMobile = document.getElementById("in-stock-checkbox-mobile");
  const outStockMobile = document.getElementById("out-of-stock-checkbox-mobile");
  if (inStockMobile) inStockMobile.checked = false;
  if (outStockMobile) outStockMobile.checked = false;

  document.querySelectorAll('#wine-types-mobile input[type="checkbox"]').forEach(cb => { cb.checked = false; });

  const priceMinInputMobile = document.getElementById("price-min-mobile");
  const priceMaxInputMobile = document.getElementById("price-max-mobile");
  const maxPrice = window.maxProductPrice || 5000;
  if (priceMinInputMobile) priceMinInputMobile.value = 0;
  if (priceMaxInputMobile) priceMaxInputMobile.value = maxPrice;

  const priceSliderMinMobile = document.getElementById("price-slider-min-mobile");
  const priceSliderMaxMobile = document.getElementById("price-slider-max-mobile");
  if (priceSliderMinMobile) priceSliderMinMobile.value = 0;
  if (priceSliderMaxMobile) priceSliderMaxMobile.value = maxPrice;

  const sortMobile = document.getElementById("sort-options-mobile");
  if (sortMobile) sortMobile.value = "default";

  if (priceMinInputMobile) priceMinInputMobile.dispatchEvent(new Event('input'));
  if (priceMaxInputMobile) priceMaxInputMobile.dispatchEvent(new Event('input'));
}

function resetAllFilters() {
  const searchInput = document.getElementById("krf-search-input");
  const clearBtn = document.getElementById("krf-clear-search");
  if (searchInput) searchInput.value = "";
  if (clearBtn) clearBtn.style.display = "none";

  window.currentSearchTerm = "";
  window.currentSearchTermNormalized = "";

  resetDesktopFilters();
  resetMobileFilters();

  const noResultsMsg = document.getElementById("no-results-message");
  if (noResultsMsg) noResultsMsg.style.display = "none";

  if (window.applyFiltersAndSort) window.applyFiltersAndSort();
}

function initResetButton() {
  const resetBtn = document.getElementById("reset-filters-btn");
  if (resetBtn) resetBtn.addEventListener("click", resetAllFilters);
}

document.addEventListener("DOMContentLoaded", () => {
  fetchCollectionProducts(window.selectedCollectionHandle);
  syncFilters();
  initResetButton();
});